import { z } from 'zod';
import { AllowlistRange, IsoTime, ServerId } from './basics.js';
import { ConfigDocument, ConfigStatusSummary } from './config.js';
import { ServerGroup, ServerGroupId } from './server.js';
import { WebSettings } from './web.js';
import { WebRuleChange, WebRules } from './web-rules.js';
import { BlockRule, BlockRuleId, BlockRuleOrigin } from './block-rules.js';
import { DomainName } from './web-domain.js';
import { CrowdsecSettingsPatch, DbExposureSettingsPatch, DbHealthSettingsPatch, DbSizeSettingsPatch, DiskSettingsPatch, SystemSettingsPatch, WazuhFimSettingsPatch, WebEnvSettingsPatch, WebTrafficSettingsPatch, } from './settings.js';
import { DEFAULT_FIM_IGNORE, DEFAULT_FIM_PATHS } from './wazuh-fim.js';
/**
 * Settings layers, and the versions they merge into (knowledge doc section 11).
 *
 * The central database holds the **desired** settings as three layers — global
 * defaults, then the server's group, then the server itself — and the API merges
 * them into one `ConfigDocument` per server. `ConfigDocument` in `config.ts` is the
 * result; this file is the input, which is a different shape for one reason worth
 * stating: a layer says what it changes and nothing else.
 */
/**
 * Section 16's `config_layers.scope_type`.
 *
 * Section 11's prose says "global defaults → role/group settings → server
 * overrides", but section 16's column lists three scopes and role is not one of
 * them. Groups carry roles (`server_groups.roles`), so a role-wide setting is a
 * group; nothing is lost and there is one fewer axis for two layers to disagree on.
 */
export const ConfigScope = z.enum(['global', 'group', 'server']);
/**
 * Which layer, and which one of it. A discriminated union rather than
 * `{ scope, id: string | null }`, so "global with an id" and "server without one"
 * are both unrepresentable instead of being checked at each call site.
 */
export const ConfigScopeRef = z.discriminatedUnion('scope', [
    // Strict, or the members would strip what does not belong instead of refusing
    // it: `{ scope: 'global', id: 'prod-web-01' }` would parse as a global layer,
    // which is exactly the confusion this union is here to prevent.
    z.object({ scope: z.literal('global') }).strict(),
    z.object({ scope: z.literal('group'), id: ServerGroupId }).strict(),
    z.object({ scope: z.literal('server'), id: ServerId }).strict(),
]);
/**
 * What one layer holds.
 *
 * Every field is optional at every level: a layer that sets only
 * `modules.disk.warn_pct` carries exactly that and inherits the rest. Absence means
 * inherit, and there is no `null` meaning "unset" — a layer stops overriding a
 * field by no longer naming it, which is what React does when a field is cleared.
 */
export const SettingsPatch = z
    .object({
    allowlist: z.array(AllowlistRange).max(500),
    modules: z
        .object({
        disk: DiskSettingsPatch,
        wazuh_fim: WazuhFimSettingsPatch,
        crowdsec: CrowdsecSettingsPatch,
        db_size: DbSizeSettingsPatch,
        db_health: DbHealthSettingsPatch,
        db_exposure: DbExposureSettingsPatch,
        web_env: WebEnvSettingsPatch,
        web_traffic: WebTrafficSettingsPatch,
        system: SystemSettingsPatch,
    })
        .partial()
        .strict(),
    /**
     * `web.environment` is whole, not merged key by key (`ATOMIC` in the API's
     * merge): half of one environment laid over half of another is neither.
     * Only its own route writes it, behind an admin's password re-check.
     */
    web: WebSettings,
})
    .partial()
    .strict();
export const LIST_MERGE = {
    allowlist: 'union',
    'modules.crowdsec.collections': 'union',
    /** Watch or enforce per scenario or collection (P11.9.2); the narrower layer's wins. */
    'modules.crowdsec.modes': { by: 'name' },
    'modules.wazuh_fim.ignore': 'union',
    /** Two layers watching `/var/www` differently is one path; the later mode wins. */
    'modules.wazuh_fim.paths': { by: 'path' },
    /** Two layers naming the same database is one target; the later one wins. */
    'modules.db_size.targets': { by: 'name' },
    /** Ports being watched are protections in force: a group adds to global's. */
    'modules.db_exposure.ports': 'union',
    /** Bot blocks are protections in force, and so is letting a bot through (P6.6). */
    'web.blocked_bots': 'union',
    'web.blocked_patterns': 'union',
    'web.allowed_bots': 'union',
    /** One limit per bot or category; the narrower layer's rate for it wins. */
    'web.rate_limits': { by: 'match' },
    /**
     * Block rules (P11.5) are one per id and site, and the lower layer's copy wins
     * — even one switched off: section 12's "Block rules" lets a narrower level
     * lift a rule a wider one set, said and audited rather than silent.
     */
    'web.block_rules': { by: ['site', 'id'] },
};
/**
 * What each role turns on (knowledge doc section 7), as a layer beneath global.
 *
 * Beneath, not above: these are defaults, so an administrator who switches a
 * module off globally has switched it off, database role or not. Several roles
 * apply in their sorted order, and since a role only ever turns things on, the
 * order cannot change the outcome.
 *
 * Every module `base` adds is on by default, so what it carries is section 8.2's
 * watched paths (P7.1). `web` turns on section 8.8's detection (P5.5) and section
 * 8.7's traffic module (P6.3), and watches the web root. The paths are here rather
 * than a schema default because `wazuh_fim.paths` merges by path: a layer adding
 * one path of its own keeps these, where it would replace a default outright.
 */
export const ROLE_DEFAULTS = {
    base: {
        modules: {
            wazuh_fim: {
                paths: DEFAULT_FIM_PATHS.base.map((entry) => ({ ...entry })),
                ignore: [...DEFAULT_FIM_IGNORE],
            },
        },
    },
    web: {
        modules: {
            web_env: { enabled: true },
            web_traffic: { enabled: true },
            wazuh_fim: { paths: DEFAULT_FIM_PATHS.web.map((entry) => ({ ...entry })) },
        },
    },
    database: {
        modules: {
            db_size: { enabled: true },
            db_health: { enabled: true },
            db_exposure: { enabled: true },
        },
    },
};
/** A stored layer, as the settings screen reads it. */
export const ConfigLayer = z.object({
    scope: ConfigScope,
    /** Null for `global`; a group id or a server id otherwise. */
    scopeId: z.string().max(64).nullable(),
    settings: SettingsPatch,
    /** The address of whoever last changed it, or null once that account is removed. */
    updatedByEmail: z.email().nullable(),
    updatedAt: IsoTime,
});
/**
 * One row of section 11's version history.
 *
 * The `hash` covers the settings and **not** the version number, so two versions
 * with the same hash are the same settings — which is what lets the API skip
 * minting a version when a save changed nothing, and what lets the history screen
 * say "no change" rather than showing an empty diff.
 */
export const ConfigVersionSummary = z.object({
    serverId: ServerId,
    version: z.number().int().positive(),
    hash: z.string().regex(/^sha256:[0-9a-f]{64}$/, 'sha256:<hex>'),
    createdAt: IsoTime,
});
/**
 * The three layers a version was merged from, stored with it.
 *
 * Section 11 promises "one-click revert to any earlier version", and this is what
 * makes that honest. A version is the *result* of merging three layers and cannot
 * be turned back into them — list unions in particular are not invertible, because
 * a layer cannot remove what another added. Writing the merged document back as a
 * new version would work exactly once, and then the next edit to any layer would
 * silently undo the revert, because the layers are the desired state and would
 * still say what they said before.
 *
 * So a revert restores the layers rather than the document. `null` for a layer
 * means it did not exist at the time, which restoring reproduces by removing it —
 * different from `{}`, a layer that existed and said nothing.
 */
export const ConfigSources = z.object({
    global: SettingsPatch.nullable(),
    group: SettingsPatch.nullable(),
    server: SettingsPatch.nullable(),
});
/** A version with everything the history screen needs to show and compare it. */
export const ConfigVersionDetail = ConfigVersionSummary.extend({
    document: ConfigDocument,
    /** Null for a version written before layers were recorded; it cannot be reverted to. */
    sources: ConfigSources.nullable(),
});
/** Reading a layer that has never been written answers `null` rather than an empty one. */
export const ConfigLayerResponse = z.object({
    layer: ConfigLayer.nullable(),
    /** How many servers this layer reaches, so an edit shows its blast radius first. */
    affectedServers: z.number().int().nonnegative(),
});
export const UpdateConfigLayerRequest = z.object({ settings: SettingsPatch });
/**
 * What a settings change did to the fleet.
 *
 * `failed` names the servers whose layers no longer merge into a valid document.
 * The change is kept rather than refused: one server with a bad override must not
 * block a global change, and refusing would leave an admin unable to fix anything
 * until they found the machine at fault.
 */
export const RebuildSummary = z.object({
    created: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
    failed: z
        .array(z.object({ serverId: ServerId, issues: z.array(z.string().max(500)).max(20) }))
        .max(100),
});
/** `PATCH /v1/server-groups/:id`: the group, and what new roles did to its servers. */
export const ServerGroupUpdateResponse = z.object({ group: ServerGroup, rebuild: RebuildSummary });
/**
 * `POST /v1/web-rules` (section 12; P6.6): one change to one layer's bot rules,
 * from the Traffic page. An operator's, except that blocking or rate-limiting a
 * verified search engine is section 12's High row: an admin, a fresh password
 * re-check, and `confirm` typed as the bot's name (`confirmationFor`).
 */
export const WebRuleChangeRequest = z
    .object({
    scope: ConfigScopeRef,
    change: WebRuleChange,
    confirm: z.string().trim().max(100).optional(),
})
    .strict();
/** One layer's rules, named for a person. */
export const WebRulesLayer = z.object({
    scope: ConfigScope,
    /** Null for `global`. */
    id: z.string().max(64).nullable(),
    /** The group's name for a group layer; null otherwise. */
    name: z.string().max(200).nullable(),
    rules: WebRules,
});
export const WebRuleChangeResponse = z.object({
    layer: WebRulesLayer,
    rebuild: RebuildSummary,
});
/**
 * `GET /v1/servers/:id/web-rules`: which layer holds which rule, so the Traffic
 * page can say "blocked for the whole fleet" and offer to lift it where it was set.
 */
export const ServerWebRules = z.object({
    serverId: ServerId,
    layers: z.array(WebRulesLayer).max(3),
    /** What the server enforces, all layers together. */
    effective: WebRules,
});
export const ConfigLayerUpdateResponse = z.object({
    layer: ConfigLayer,
    rebuild: RebuildSummary,
});
/** One field that differs between two versions (section 11's before/after). */
export const ConfigChange = z.object({
    /** Dotted, as the settings are addressed everywhere else: `modules.disk.warn_pct`. */
    path: z.string().min(1).max(200),
    kind: z.enum(['added', 'removed', 'changed']),
    /**
     * `.optional()` is load-bearing, and not obviously so: in zod 4 a bare
     * `z.unknown()` is **required**, so an added field — which has no `before` — fails
     * the schema. That is a 500 on the response it describes rather than a type
     * error, and it only shows up for a diff that added or removed something.
     */
    before: z.unknown().optional(),
    after: z.unknown().optional(),
});
export const ConfigDiff = z.object({
    serverId: ServerId,
    from: z.number().int().positive(),
    to: z.number().int().positive(),
    changes: z.array(ConfigChange).max(500),
});
export const ConfigDiffQuery = z.object({
    server_id: ServerId,
    from: z.coerce.number().int().positive(),
    to: z.coerce.number().int().positive(),
});
export const RevertConfigRequest = z.object({
    serverId: ServerId,
    /** The version whose layers should be put back. */
    version: z.number().int().positive(),
});
export const ServerConfigStatus = ConfigStatusSummary.extend({
    serverId: ServerId,
    name: z.string().min(1).max(255),
});
/**
 * Block rules (section 12, "Block rules"; P11.5): one layer's rules, as the Rules
 * page reads and changes them. A change is one rule at a time — saved by its id
 * and site, or removed — so two operators changing two rules both get their way.
 */
export const BlockRulesQuery = z
    .object({ scope: ConfigScope, id: z.string().min(1).max(64).optional() })
    .strict()
    .refine((query) => (query.scope === 'global') === (query.id === undefined), {
    message: 'a group or server layer is named by its id; the global one by none',
});
export const BlockRulesLayer = z.object({
    scope: ConfigScope,
    id: z.string().max(64).nullable(),
    /** The group's or server's name; null for global. */
    name: z.string().max(255).nullable(),
    rules: z.array(BlockRule).max(100),
    /** How many servers this layer reaches. */
    affectedServers: z.number().int().nonnegative(),
});
export const BlockRuleSaveRequest = z.object({ scope: ConfigScopeRef, rule: BlockRule }).strict();
export const BlockRuleRemoveRequest = z
    .object({ scope: ConfigScopeRef, id: BlockRuleId, site: DomainName.optional() })
    .strict();
/**
 * `POST /v1/block-rules/promote` (P11.6): move one rule from a server's or a
 * group's layer to a wider one — a group's or the global — in one step, so a rule
 * proved on one machine becomes the fleet's without being written twice.
 */
export const BlockRulePromoteRequest = z
    .object({
    from: ConfigScopeRef,
    to: ConfigScopeRef,
    id: BlockRuleId,
    site: DomainName.optional(),
})
    .strict()
    .refine(({ from, to }) => (from.scope === 'server' && to.scope !== 'server') ||
    (from.scope === 'group' && to.scope === 'global'), {
    message: 'a rule moves to a wider layer: a server to its group or global, a group to global',
});
export const BlockRuleChangeResponse = z.object({
    layer: BlockRulesLayer,
    rebuild: RebuildSummary,
});
/** A rule a site runs, and the layer it came from. */
export const EffectiveBlockRule = z.object({ rule: BlockRule, origin: BlockRuleOrigin });
/**
 * `GET /v1/servers/:id/block-rules`: what each site on a server runs and where
 * each rule came from. `host` null is every site without copies of its own;
 * `hosts` are the sites seen in its traffic over the last day, for the picker.
 */
export const ServerBlockRules = z.object({
    serverId: z.string().max(64),
    sites: z
        .array(z.object({ host: DomainName.nullable(), rules: z.array(EffectiveBlockRule).max(300) }))
        .max(301),
    hosts: z.array(z.string().max(253)).max(50),
});
/** `POST /v1/block-rules/preview`: what a rule would have matched over the last day. */
export const BlockRulePreviewRequest = z
    .object({ scope: ConfigScopeRef, rule: BlockRule })
    .strict();
/** What a CrowdSec trigger would have acted on, from the alerts the servers already raised (P11.16). */
export const TriggerPreview = z.object({
    /** Alerts for its scenarios in the window. */
    alerts: z.number().int().nonnegative(),
    addresses: z.number().int().nonnegative(),
    servers: z.number().int().nonnegative(),
    /** Of those addresses, how many a ban already stopped (the scenario banned them itself). */
    alreadyBanned: z.number().int().nonnegative(),
    /** And how many the rule would have banned that nothing did. */
    wouldBan: z.number().int().nonnegative(),
    /** Some of the addresses it would have banned, the busiest first. */
    examples: z.array(z.string().max(64)).max(10),
});
export const BlockRulePreview = z.object({
    from: IsoTime,
    to: IsoTime,
    /** Requests to the busiest paths this rule's paths match: a floor, not a count. */
    requests: z.number().int().nonnegative(),
    status4xx: z.number().int().nonnegative(),
    paths: z
        .array(z.object({
        path: z.string().max(256),
        requests: z.number().int().nonnegative(),
        status4xx: z.number().int().nonnegative(),
    }))
        .max(20),
    /** For a CrowdSec trigger: what it would have acted on, in place of the request figures. */
    trigger: TriggerPreview.optional(),
});
//# sourceMappingURL=config-layer.js.map